import React, { useCallback, useEffect, useState } from 'react';
import { QuickTaskInput } from '../types';
import { useProjects } from '../contexts/ProjectContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { QuickTaskModal } from '../components/common/QuickTaskModal';

/**
 * Alta rápida de tarea desde cualquier página, con atajo global "c".
 *
 * Centraliza modal, estado y atajo para que las páginas solo tengan que
 * renderizar `modal` y, si quieren, un botón que llame a `open`.
 */
export function useQuickTask() {
  const { activeProject, activeProjectMembers, createQuickTask } = useProjects();
  const { isProjectManager } = useAuth();
  const { showToast } = useToast();
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  // Atajo "c" — ignorado mientras se escribe, para no abrir el modal
  // en medio de un comentario.
  useEffect(() => {
    if (!isProjectManager) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'c' && e.key !== 'C') return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) return;

      e.preventDefault();
      setIsOpen(true);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isProjectManager]);

  const handleSubmit = useCallback(async (task: QuickTaskInput) => {
    if (!activeProject) return { success: false, error: 'No active project.' };
    const result = await createQuickTask(activeProject.id, task);
    if (result.success) showToast('Task created', 'success');
    return result;
  }, [activeProject, createQuickTask, showToast]);

  const modal = (
    <QuickTaskModal
      isOpen={isOpen}
      project={activeProject}
      members={activeProjectMembers}
      onSubmit={handleSubmit}
      onClose={close}
    />
  );

  return { open, close, isOpen, modal, canCreate: isProjectManager && !!activeProject };
}
